import React, { useEffect } from 'react';
import PIEDialog from '@src/shared/components/PIEDialog';
import PIEText from '@src/shared/components/PIEText';
import PIEButton from '@src/shared/components/PIEButton';
import { IconClose } from '@justeattakeaway/pie-icons-react';
import styles from './DashboardTutorialFinalPopup.module.scss';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import DashboardAdvertiseCard from '@src/pages/DeliveryDashboard/components/DashboardAdvertiseCard';
import CommissionSmallIcon from '@assets/commission-small.png';
import LightCelebrateLarge from '@assets/light-celebrate-6-large.png';
import ErrorSorry from '@assets/error-sorry-1-small.png';
import { useTranslation } from 'react-i18next';
import PIENotification, { IPieNotificationMode } from '@src/shared/components/PIENotification';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import ProgrammaticalMobileScroller from '@src/shared/components/ProgrammaticalMobileScroller';
import useBannerTracking from '@src/shared/hooks/use-banner-tracking';
import useOnboardingTracking from '@src/pages/DeliveryDashboard/components/DashboardOnboarding/tracking/use-onboarding-tracking';
import { useOnboardingStore } from '@src/shared/store/onboarding/onboarding-store';

interface componentProps {
  onClose: () => void;
  onReplay: () => void;
}

const DashboardTutorialFinalPopup = ({ onClose, onReplay }: componentProps) => {
  const { t } = useTranslation();
  const { isResolutionMobileInstantCheck } = useDeviceDetect();
  const { trackBannerView } = useBannerTracking('onboarding - mcap benefits');
  const {
    trackProductOverviewLink,
    trackFinalTutorialDialogClose,
    trackReviewTutorial,
    trackFinalTutorialDialogEnd,
  } = useOnboardingTracking();
  const { setReminderActive, setActiveStep } = useOnboardingStore();

  useEffect(() => {
    trackBannerView();
  }, []);
  const adveritseCardContent = (
    <PIEComponentUtil marginX="var(--xds-spacing-d)">
      <PIEComponentUtil marginBottom="var(--xds-spacing-b)">
        <DashboardAdvertiseCard
          elevation={false}
          image={<img src={CommissionSmallIcon} width={64} height={64} />}
          title={
            <PIEText
              text={t('onboarding_dashboard.POPUP_ADVERTISE_1_TITLE')}
              color="var(--xds-color-content-default)"
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
            />
          }
          description={
            <PIEText
              text={t('onboarding_dashboard.POPUP_ADVERTISE_1_DESCRIPTION')}
              color="var(--xds-color-content-default)"
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-regular)"
            />
          }
        />
      </PIEComponentUtil>
      <PIEComponentUtil marginBottom="var(--xds-spacing-b)">
        <DashboardAdvertiseCard
          image={<img src={LightCelebrateLarge} width={64} height={64} />}
          elevation={false}
          title={
            <PIEText
              text={t('onboarding_dashboard.POPUP_ADVERTISE_2_TITLE')}
              color="var(--xds-color-content-default)"
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
            />
          }
          description={
            <PIEText
              text={t('onboarding_dashboard.POPUP_ADVERTISE_2_DESCRIPTION')}
              color="var(--xds-color-content-default)"
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-regular)"
            />
          }
        />
      </PIEComponentUtil>
      <PIEComponentUtil marginBottom="var(--xds-spacing-b)">
        <DashboardAdvertiseCard
          image={<img src={ErrorSorry} width={64} height={64} />}
          elevation={false}
          title={
            <PIEText
              text={t('onboarding_dashboard.POPUP_ADVERTISE_3_TITLE')}
              color="var(--xds-color-content-default)"
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
            />
          }
          description={
            <PIEText
              text={t('onboarding_dashboard.POPUP_ADVERTISE_3_DESCRIPTION')}
              color="var(--xds-color-content-default)"
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-regular)"
            />
          }
        />
      </PIEComponentUtil>

      <PIENotification
        modInline={true}
        showClose={false}
        isOpen={true}
        mode={IPieNotificationMode.Info}
      >
        {t('onboarding_dashboard.POPUP_INFO_CONTENT')}{' '}
        <u
          className={styles.externalLink}
          onClick={() => {
            trackProductOverviewLink();
            onClose();
            setActiveStep(1);
            location.href = '/product-overview';
          }}
        >
          <b>{t('onboarding_dashboard.POPUP_INFO_LINK')}</b>
        </u>
      </PIENotification>
    </PIEComponentUtil>
  );
  return (
    <PIEDialog
      modCustom
      closeOnOutside={false}
      onClose={() => {
        setReminderActive(true);
        onClose();
        setActiveStep(1);
      }}
      noPadding={true}
      header={
        <div className={styles.PopupHeader}>
          <PIEText
            text={t('onboarding_dashboard.POPUP_TITLE')}
            color="var(--xds-color-content-default)"
            size="var(--xds-font-size-24)"
            fontWeight="var(--xds-font-weight-bold)"
          />
          <IconClose
            size="m"
            onClick={() => {
              trackFinalTutorialDialogClose();
              onClose();
              setActiveStep(1);
            }}
          />
        </div>
      }
      footer={
        <div className={styles.PopupFooter}>
          <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
            <PIEButton
              type="submit"
              mode="primary"
              size="small"
              text={t('onboarding_dashboard.POPUP_BUTTON_END')}
              onClick={() => {
                setReminderActive(true);
                trackFinalTutorialDialogEnd();
                onClose();
                setActiveStep(1);
              }}
              autoWidth
            />
          </PIEComponentUtil>
          <PIEButton
            type="submit"
            mode="ghost"
            size="small"
            text={t('onboarding_dashboard.POPUP_BUTTON_REPLAY')}
            onClick={() => {
              trackReviewTutorial();
              onReplay();
            }}
            autoWidth
          />
        </div>
      }
      noContentPadding={true}
      isCenter
      isOpen={true}
      size="small"
    >
      <PIEComponentUtil marginX={'var(--xds-spacing-d)'} marginBottom={'var(--xds-spacing-d)'}>
        <PIEText
          text={t('onboarding_dashboard.POPUP_SUB_TITLE')}
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-20)"
          fontWeight="var(--xds-font-weight-bold)"
        />
      </PIEComponentUtil>
      {isResolutionMobileInstantCheck() && screen.height <= 900 ? (
        <ProgrammaticalMobileScroller height={300}>
          {adveritseCardContent}
        </ProgrammaticalMobileScroller>
      ) : (
        adveritseCardContent
      )}
    </PIEDialog>
  );
};

export default DashboardTutorialFinalPopup;
